import { useMemo } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiClose, mdiViewColumnOutline, mdiViewListOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { CategoryCards } from './CategoryCards'
import { ClaimPipeline, type ClaimTicket } from './ClaimPipeline'
import { TicketQueue, DEFAULT_STATUS, toCounts } from '../tickets/TicketQueue'
import { isSteeringCategory, type SteeringCategory } from './steering-categories'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { DigestInvite } from '../notifications/DigestInvite'
import { useMarkIssuesSeen } from '../../hooks/useAttentionCounts'
import { useMemberKind } from '../../contexts/PermissionsContext'

/**
 * Steering — the three families of work a property manager owns: economics,
 * incidents, compliance.
 *
 * Data quality is deliberately NOT here (spec 2026-08-24-03, lot 10). It first
 * lived on this page as a demoted footer strip, on the theory that it should be
 * reachable without competing. Seen on real data the theory did not survive:
 * the pile outnumbers the three categories ten to one, and demoting it on the
 * page it does not belong to is a compromise rather than an answer. Data
 * stewardship is not asset management, so it moved to the Data section, where
 * import and export already live. This page now has exactly three categories
 * and no fourth thing.
 */

/** Bookmarks predating the three-category axis carried a `nature` (the
 * diagnostic type) or `tab=sinistres`. Map them rather than dropping the user
 * on an unrelated category. `data_consistency` no longer has a home here — the
 * route redirects it to the Data section instead. */
const LEGACY_NATURE_CATEGORY: Record<string, SteeringCategory> = {
    suggestion: 'economics',
    compliance: 'compliance',
}

export function SteeringPage() {
    const { org } = useParams({ from: '/$org/steering' })
    const search = useSearch({ from: '/$org/steering' })
    const navigate = useNavigate({ from: '/$org/steering' })
    const { t } = useTranslation('issues')

    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('steering.title') },
    ])

    const legacyCategory = search.tab === 'sinistres' ? 'incidents' : LEGACY_NATURE_CATEGORY[search.nature ?? '']
    const category: SteeringCategory = isSteeringCategory(search.category) ? search.category : (legacyCategory ?? 'economics')
    // The claim pipeline is a *view* of Incidents, not a top-level tab: a claim
    // is an incident, and a national insurance workflow has no business sitting
    // beside the three categories.
    const view = category === 'incidents' && (search.view === 'pipeline' || (search.tab === 'sinistres' && !search.view)) ? 'pipeline' : 'list'
    const selected = search.selected ?? null
    const statusFilter = search.status ?? DEFAULT_STATUS
    const sort = search.sort === 'due' || search.sort === 'recent' ? search.sort : 'priority'
    const page = Math.max(1, Number(search.page) || 1)
    // Arriving through the sidebar badge (spec 2026-10-27-23): the URL keeps
    // the marker the badge counted from, so the list still shows what was
    // counted after this visit moves it.
    const attention = (search as { attention?: string }).attention
    const markIssuesSeen = useMarkIssuesSeen(org)
    const isStaff = useMemberKind() === 'staff'

    /** Any change but paging or selecting sends the user back to page 1 — a
     * filter change makes the current offset meaningless. */
    const setSearch = (next: Record<string, string | number | undefined>) => {
        const resetsPage = Object.keys(next).some((k) => k !== 'page' && k !== 'selected')
        navigate({
            search: (prev: Record<string, unknown>) =>
                ({
                    ...prev,
                    ...(resetsPage ? { page: undefined } : {}),
                    // Legacy params are consumed on read; never carry them forward.
                    tab: undefined,
                    nature: undefined,
                    ...next,
                }) as never,
            replace: true,
        })
    }
    const select = (uid: string | null) => {
        navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, selected: uid ?? undefined }) as never, replace: false })
    }
    const selectCategory = (next: SteeringCategory) => {
        setSearch({ category: next, subcategory: undefined, selected: undefined, view: undefined })
    }

    // ---- The cards: counted server side, over the whole result set. Counting
    // the rows the client happens to hold is how the old page ended up
    // announcing "20" for an organization carrying several thousand tickets.
    const categoryStats = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues/stats', {
        params: {
            path: { orgCode: org },
            query: { group_by: 'category', status: statusFilter, assignee_uid: 'all', roots_only: search.children !== 1 },
        },
    })
    const urgentStats = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues/stats', {
        params: {
            path: { orgCode: org },
            query: { group_by: 'category', status: statusFilter, priority: '1,2', assignee_uid: 'all', roots_only: search.children !== 1 },
        },
    })

    // The scoped asset, so the chip below can name it. A filter the user cannot
    // see is a filter they cannot undo — and the cards above still show
    // org-wide totals, so without the chip a scoped queue just looks wrong.
    const scopedAsset = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}',
        { params: { path: { orgCode: org, assetUid: search.asset ?? '' } } },
        { enabled: !!search.asset },
    )

    // Claim dossiers, fetched only when the pipeline view is on screen.
    const claimsQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues',
        {
            params: {
                path: { orgCode: org },
                query: { type: 'claim', status: 'open,in_progress,closed', assignee_uid: 'all', limit: 100 } as unknown as Record<string, unknown>,
            },
        },
        { enabled: view === 'pipeline' },
    )

    const countsByCategory = useMemo(() => toCounts(categoryStats.data?.data, 'category'), [categoryStats.data])
    const urgentByCategory = useMemo(() => toCounts(urgentStats.data?.data, 'category'), [urgentStats.data])
    const claims = (claimsQuery.data?.data ?? []) as ClaimTicket[]

    const viewToggle = category === 'incidents' && (
        <div className="flex items-center rounded-md border p-0.5 ml-auto" data-testid="steering-view-toggle">
            <button
                type="button"
                onClick={() => setSearch({ view: undefined })}
                aria-pressed={view === 'list'}
                aria-label={t('steering.view_list')}
                title={t('steering.view_list')}
                className={`p-1.5 rounded ${view === 'list' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="steering-view-list"
            >
                <AppIcon path={mdiViewListOutline} size="xs" />
            </button>
            <button
                type="button"
                onClick={() => setSearch({ view: 'pipeline' })}
                aria-pressed={view === 'pipeline'}
                aria-label={t('steering.view_pipeline')}
                title={t('steering.view_pipeline')}
                className={`p-1.5 rounded ${view === 'pipeline' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="steering-view-pipeline"
            >
                <AppIcon path={mdiViewColumnOutline} size="xs" />
            </button>
        </div>
    )

    return (
        <div data-testid="steering-page">
            <PageHeader
                icon={SECTION_ICONS.steering}
                title={t('steering.title')}
                description={t('steering.subtitle')}
                className="mb-4"
            />

            {isStaff && <DigestInvite org={org} />}

            <CategoryCards
                active={category}
                counts={countsByCategory}
                urgentCounts={urgentByCategory}
                loading={categoryStats.isLoading}
                onSelect={selectCategory}
            />

            {view === 'pipeline' ? (
                <>
                    {/* In pipeline view the filter bar would drive nothing: the
                        pipeline reads the claim dossiers, not the queue. Only
                        the view toggle stays. */}
                    <div className="flex items-center gap-2 mb-3">{viewToggle}</div>
                    <ClaimPipeline claims={claims} selected={selected} onSelect={select} />
                </>
            ) : (
                <>
                {attention && (
                    <div className="flex items-center gap-2 mb-3">
                        <span
                            className="inline-flex items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/5 px-2.5 py-1 text-xs text-foreground"
                            data-testid="steering-attention-chip"
                        >
                            {t('steering.attention_chip')}
                            <button
                                type="button"
                                onClick={() => setSearch({ attention: undefined })}
                                aria-label={t('steering.attention_clear')}
                                title={t('steering.attention_clear')}
                                className="text-muted-foreground hover:text-foreground"
                                data-testid="steering-attention-chip-clear"
                            >
                                <AppIcon path={mdiClose} size="xs" />
                            </button>
                        </span>
                    </div>
                )}
                {search.asset && (
                    <div className="flex items-center gap-2 mb-3" data-testid="steering-asset-scope">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs text-foreground">
                            {/* The chip names the asset the queue is scoped to,
                                so it is also the shortest way to that asset's
                                page — a filter you can read but not follow is a
                                dead end (#1339). */}
                            <Link
                                to="/$org/assets/$assetUid"
                                params={{ org, assetUid: search.asset }}
                                className="hover:underline"
                                data-testid="steering-asset-scope-link"
                            >
                                {t('steering.scoped_to', { name: scopedAsset.data?.name ?? '…' })}
                            </Link>
                            <button
                                type="button"
                                onClick={() => setSearch({ asset: undefined })}
                                aria-label={t('steering.clear_asset_scope')}
                                title={t('steering.clear_asset_scope')}
                                className="text-muted-foreground hover:text-foreground"
                                data-testid="steering-asset-scope-clear"
                            >
                                <AppIcon path={mdiClose} size="xs" />
                            </button>
                        </span>
                    </div>
                )}
                <TicketQueue
                    org={org}
                    category={category}
                    // Org-wide total only when the queue is org-wide. Scoped to
                    // one asset, the cards above still count the whole org, so
                    // handing their number to the "All" pill would contradict
                    // the rows beneath it — the facet sums itself instead.
                    categoryTotal={search.asset ? undefined : (countsByCategory[category] ?? 0)}
                    filters={{ asset: search.asset, subtree: search.subtree !== 0, due: search.due, attention }}
                    onLoaded={markIssuesSeen}
                    state={{
                        subcategory: search.subcategory,
                        status: statusFilter,
                        sort,
                        q: search.q ?? '',
                        page,
                        selected,
                        children: search.children === 1,
                        followUp: search.follow_up,
                    }}
                    onChange={setSearch}
                    onSelect={select}
                    toolbarExtra={viewToggle}
                />
                </>
            )}
        </div>
    )
}
